import { useMemo } from 'react';
import { useApiQuery } from './useApiQuery';
import { queryKeys } from './queryKeys';
import { fundService } from '@/services/fundService';
import { safeParseNumber } from '@/utils/formatters';

export type WaterfallViewType = 'realised' | 'unrealised';

interface WaterfallTierResult {
  tier_number: number;
  tier_name: string;
  lp_amount: string;
  gp_amount: string;
  cumulative_lp: string;
  cumulative_gp: string;
}

interface DistributionWaterfallResult {
  total_distribution_amount: string;
  called_capital: string;
  preferred_return_amount: string;
  tiers: WaterfallTierResult[];
  total_lp: string;
  total_gp: string;
  total_profit: string;
  lp_percentage: string;
  gp_percentage: string;
  hurdle_rate: string;
  carried_interest: string;
  catch_up_rate: string;
  days_elapsed?: number;
}

interface LPWaterfallResult {
  investor_id: string;
  investor_name: string;
  waterfall: DistributionWaterfallResult;
}

interface FundWaterfallResult {
  overall: DistributionWaterfallResult;
  investors: LPWaterfallResult[];
}

// ── Parsed (numeric) types ──────────────────────────────────────────
// API serialises Python Decimals as strings.  The hook parses them once
// so every downstream component receives plain numbers.

interface ParsedWaterfallTierResult {
  tier_number: number;
  tier_name: string;
  lp_amount: number;
  gp_amount: number;
  cumulative_lp: number;
  cumulative_gp: number;
}

export interface ParsedDistributionWaterfallResult {
  total_distribution_amount: number;
  called_capital: number;
  preferred_return_amount: number;
  tiers: ParsedWaterfallTierResult[];
  total_lp: number;
  total_gp: number;
  total_profit: number;
  lp_percentage: number;
  gp_percentage: number;
  hurdle_rate: number;
  carried_interest: number;
  catch_up_rate: number;
  days_elapsed?: number;
}

export interface ParsedLPWaterfallResult {
  investor_id: string;
  investor_name: string;
  waterfall: ParsedDistributionWaterfallResult;
}

export interface ParsedFundWaterfallResult {
  overall: ParsedDistributionWaterfallResult;
  investors: ParsedLPWaterfallResult[];
}

// ── Transform helpers ───────────────────────────────────────────────

function parseTier(t: WaterfallTierResult): ParsedWaterfallTierResult {
  return {
    tier_number: t.tier_number,
    tier_name: t.tier_name,
    lp_amount: safeParseNumber(t.lp_amount),
    gp_amount: safeParseNumber(t.gp_amount),
    cumulative_lp: safeParseNumber(t.cumulative_lp),
    cumulative_gp: safeParseNumber(t.cumulative_gp),
  };
}

function parseDistribution(
  d: DistributionWaterfallResult
): ParsedDistributionWaterfallResult {
  return {
    total_distribution_amount: safeParseNumber(d.total_distribution_amount),
    called_capital: safeParseNumber(d.called_capital),
    preferred_return_amount: safeParseNumber(d.preferred_return_amount),
    tiers: d.tiers.map(parseTier),
    total_lp: safeParseNumber(d.total_lp),
    total_gp: safeParseNumber(d.total_gp),
    total_profit: safeParseNumber(d.total_profit),
    lp_percentage: safeParseNumber(d.lp_percentage),
    gp_percentage: safeParseNumber(d.gp_percentage),
    hurdle_rate: safeParseNumber(d.hurdle_rate),
    carried_interest: safeParseNumber(d.carried_interest),
    catch_up_rate: safeParseNumber(d.catch_up_rate),
    days_elapsed: d.days_elapsed,
  };
}

function parseWaterfallResult(
  raw: FundWaterfallResult
): ParsedFundWaterfallResult {
  return {
    overall: parseDistribution(raw.overall),
    investors: raw.investors.map((lp) => ({
      investor_id: lp.investor_id,
      investor_name: lp.investor_name,
      waterfall: parseDistribution(lp.waterfall),
    })),
  };
}

/**
 * Query hook to auto-fetch the distribution waterfall for a fund.
 * Returns null if no distributions exist yet.
 *
 * All string-encoded Decimals from the API are converted to numbers
 * so components receive ready-to-use numeric values.
 */
export function useDistributionWaterfall(
  fundId: string | undefined,
  type: WaterfallViewType = 'realised'
) {
  const query = useApiQuery<FundWaterfallResult | null>(
    queryKeys.funds.distributionWaterfall(fundId || '', type),
    () =>
      fundService.getDistributionWaterfall(
        fundId!,
        type
      ) as Promise<FundWaterfallResult | null>,
    {
      enabled: !!fundId,
    }
  );

  const parsed = useMemo<ParsedFundWaterfallResult | null | undefined>(() => {
    if (query.data === null || query.data === undefined) return query.data;
    return parseWaterfallResult(query.data);
  }, [query.data]);

  return { ...query, data: parsed };
}
